import React, { useState, useRef } from 'react'
import { Upload, FileText, MapPin, CheckCircle2, AlertCircle, Loader2 } from '@/ui/icons'
import { Button } from '@/ui/components'
import { parseGpxFile, type ParsedWaypoint } from '@/shared/utils/gpxParser'
import { RoutesApi } from '@/shared/api/routes'
import { useAuth } from '@/modules/auth'

interface GpxImporterProps {
  onImportComplete?: () => void
}

export function GpxImporter({ onImportComplete }: GpxImporterProps) {
  const { getAccessToken } = useAuth()
  const [isParsing, setIsParsing] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [parsedData, setParsedData] = useState<{
    waypoints: ParsedWaypoint[]
    routePoints: ParsedWaypoint[]
    routeName?: string
    routeDescription?: string
    metadata?: Record<string, any>
  } | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [rawGpxXml, setRawGpxXml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const routesApi = React.useMemo(() => new RoutesApi(getAccessToken), [getAccessToken])

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setError(null)

    if (!file.name.toLowerCase().endsWith('.gpx')) {
      setError('Please select a file with a .gpx extension.')
      return
    }

    setSelectedFile(file)
    setIsParsing(true)

    try {
      // Keep the raw XML — the backend re-parses it server-side on import.
      // The client-side parse below is only used for the preview.
      const xml = await file.text()
      setRawGpxXml(xml)
      const parsed = await parseGpxFile(file)
      setParsedData(parsed)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read the GPX file.')
      setSelectedFile(null)
      setRawGpxXml(null)
      setParsedData(null)
    } finally {
      setIsParsing(false)
    }
  }

  const handleImport = async () => {
    if (!parsedData || !selectedFile || !rawGpxXml) {
      return
    }

    if (parsedData.waypoints.length === 0) {
      setError('No waypoints found in this GPX file.')
      return
    }

    setError(null)
    setIsImporting(true)

    try {
      // The backend parses the raw GPX server-side and creates a library folder.
      const result = await routesApi.importGpx(selectedFile.name, rawGpxXml)

      if (result.success) {
        handleClear()

        if (onImportComplete) {
          onImportComplete()
        }
      } else {
        throw new Error('Import failed')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import the GPX file.')
    } finally {
      setIsImporting(false)
    }
  }

  const handleClear = () => {
    setSelectedFile(null)
    setRawGpxXml(null)
    setParsedData(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-foreground mb-2">Import GPX File</h3>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Upload a GPX file from OnX, Gaia, or other apps. Waypoints will be extracted and added to your trip.
        </p>
      </div>

      {error && (
        <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg dark:bg-red-950/35 dark:border-red-800/60">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-800 dark:text-red-100/95">{error}</p>
        </div>
      )}

      {!parsedData ? (
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".gpx"
            onChange={handleFileSelect}
            className="hidden"
            id="gpx-file-input"
          />
          <Button
            variant="outline"
            className="w-full"
            onClick={() => fileInputRef.current?.click()}
            disabled={isParsing}
          >
            {isParsing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Parsing GPX...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Select GPX File
              </>
            )}
          </Button>
          {selectedFile && (
            <div className="flex items-center justify-between p-2 bg-muted/50 rounded-md border border-border">
              <div className="flex items-center space-x-2 min-w-0">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm text-foreground truncate">{selectedFile.name}</span>
              </div>
              <button
                onClick={handleClear}
                className="text-xs text-muted-foreground hover:text-foreground shrink-0"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-950/35 dark:border-green-800/60">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-900 dark:text-green-100">
                GPX File Parsed Successfully
              </span>
            </div>
            <div className="space-y-1 text-sm text-green-800 dark:text-green-200/95">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>{parsedData.waypoints.length} waypoint(s) found</span>
              </div>
              {parsedData.routePoints.length > 0 && (
                <div className="flex items-center space-x-2 text-amber-700 dark:text-amber-300">
                  <AlertCircle className="w-4 h-4" />
                  <span>
                    {parsedData.routePoints.length} route point(s) found (not imported)
                  </span>
                </div>
              )}
              {parsedData.routeName && (
                <div>
                  <span className="font-medium">Route:</span> {parsedData.routeName}
                </div>
              )}
            </div>
          </div>
          
          {parsedData.routePoints.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-950/35 dark:border-amber-800/60">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-800 dark:text-amber-100/95">
                  <p className="font-medium mb-1">Route points detected</p>
                  <p>
                    This GPX file contains {parsedData.routePoints.length} route point(s) that define the route path. 
                    These are not imported as waypoints. Only waypoints (&lt;wpt&gt;) are imported.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto border border-border rounded-lg">
            <div className="p-2 bg-muted/50 border-b border-border">
              <h4 className="text-xs font-medium text-foreground">Waypoint Preview</h4>
            </div>
            <div className="divide-y divide-border">
              {parsedData.waypoints.slice(0, 10).map((wp, idx) => (
                <div key={idx} className="p-2 text-xs">
                  <div className="font-medium text-foreground">{wp.name || `Waypoint ${idx + 1}`}</div>
                  <div className="text-muted-foreground">
                    {wp.lat.toFixed(6)}, {wp.lon.toFixed(6)}
                  </div>
                  {wp.notes && (
                    <div className="text-muted-foreground/90 mt-1 truncate">{wp.notes}</div>
                  )}
                </div>
              ))}
              {parsedData.waypoints.length > 10 && (
                <div className="p-2 text-xs text-muted-foreground text-center">
                  ... and {parsedData.waypoints.length - 10} more waypoint(s)
                </div>
              )}
            </div>
          </div>

          <div className="flex space-x-2">
            <Button
              variant="default"
              className="flex-1"
              onClick={handleImport}
              disabled={isImporting}
            >
              {isImporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                `Import ${parsedData.waypoints.length} Waypoint(s)`
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleClear}
            >
              Clear
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

